"use client";

import {
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Variants,
} from "framer-motion";
import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Site-wide fade-in defaults — subtle, elegant, reusable.
// Import the `FadeIn` component for one-off reveals, or the variants below when
// you need a coordinated stagger (`fadeStagger` parent + `fadeInUp` children).
// ─────────────────────────────────────────────────────────────────────────────

export const FADE_EASE = [0.15, 0, 0.55, 1] as const; // easeOut, gentle settle
export const FADE_DURATION = 0.6;

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: FADE_DURATION, ease: FADE_EASE },
  },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: FADE_DURATION, ease: FADE_EASE },
  },
};

/** Parent that reveals its children one after another. Pair with `fadeInUp`. */
export const fadeStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

type FadeInProps = {
  children: React.ReactNode;
  /** Seconds to wait before this element starts animating. */
  delay?: number;
  /** Rise distance in px (0 = pure fade). */
  y?: number;
  duration?: number;
  /** Only animate the first time it enters the viewport. */
  once?: boolean;
  /** Fraction of the element that must be visible before it triggers. */
  amount?: number;
  /**
   * Play on mount instead of on entering the viewport. For content that is
   * already on screen at load — a hero — where waiting on an intersection
   * callback reads as the reveal depending on a scroll that never comes.
   */
  immediate?: boolean;
} & Omit<
  HTMLMotionProps<"div">,
  "initial" | "animate" | "whileInView" | "variants" | "transition"
>;

/**
 * Default reveal used across the site: fades + rises into view once, and
 * automatically no-ops when the user prefers reduced motion.
 */
export function FadeIn({
  children,
  delay = 0,
  y = 24,
  duration = FADE_DURATION,
  once = true,
  amount = 0.2,
  immediate = false,
  ...props
}: FadeInProps) {
  const reduce = useReducedMotion();
  const shown = { opacity: 1, y: 0 };

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y }}
      {...(immediate
        ? { animate: shown }
        : { whileInView: shown, viewport: { once, amount } })}
      transition={{ duration, delay, ease: FADE_EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
}
